import type { TabName } from "@/components/environments/SuperOnaConversationHeader";
import type { EnvironmentViewMode } from "@/components/ai/ViewModeToggle";
import type { ActiveLogView } from "@/atoms/ui-state";
import type { RightPanelTab } from "@/routes/environments/details/SuperOnaRightPanelHeader";
import type { ChangesScope } from "@/routes/environments/code-changes/diff-mode";
import type { DrawerTab } from "@/routes/environments/details/SuperOnaEnvironmentBottomDrawer";
import { useCallback, useEffect, useRef } from "react";

/**
 * Shape of the per-environment tab state persisted to sessionStorage.
 */
export type PersistedTabState = {
    activeTab: TabName;
    selectedFilePath: string | null;
    isEnvironmentTabOpen: boolean;
    activeLogView: ActiveLogView | null;
    viewMode: EnvironmentViewMode;
    /** Active tab in the right sidebar (Changes / All Files / Comments). */
    rightPanelTab: RightPanelTab;
    /** Sub-selector within the Changes tab (Uncommitted / All changes). */
    changesScope: ChangesScope;
    /** Active tab in the bottom drawer (Ports & Services / Tasks / Terminal). */
    drawerTab: DrawerTab;
    /** ID of the selected agent execution (conversation tab). */
    selectedExecutionId: string | null;
};

const STORAGE_KEY_PREFIX = "env-tabs:";

function storageKey(environmentId: string): string {
    return `${STORAGE_KEY_PREFIX}${environmentId}`;
}

/**
 * Read persisted tab state for an environment from sessionStorage.
 * Returns null if nothing is stored or the stored value is invalid.
 * Missing sidebar fields are filled with defaults for backward compatibility.
 */
export function readEnvironmentTabState(environmentId: string): PersistedTabState | null {
    try {
        const raw = sessionStorage.getItem(storageKey(environmentId));
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (!isPersistedTabState(parsed)) return null;
        // Fill defaults for sidebar fields added after the initial schema.
        return { ...DEFAULT_STATE, ...parsed };
    } catch {
        return null;
    }
}

/**
 * Write persisted tab state for an environment to sessionStorage.
 */
export function writeEnvironmentTabState(environmentId: string, state: PersistedTabState): void {
    try {
        sessionStorage.setItem(storageKey(environmentId), JSON.stringify(state));
    } catch {
        // sessionStorage may be full or unavailable — silently ignore.
    }
}

const DEFAULT_STATE: PersistedTabState = {
    activeTab: "conversation",
    selectedFilePath: null,
    isEnvironmentTabOpen: false,
    activeLogView: null,
    viewMode: "details",
    rightPanelTab: "changes",
    changesScope: "uncommitted",
    drawerTab: "ports-services",
    selectedExecutionId: null,
};

/**
 * Merge partial updates into the persisted state for an environment.
 * Reads the current stored state, applies the partial update, and writes back.
 * If no state exists yet, uses defaults for missing fields.
 */
export function mergeEnvironmentTabState(environmentId: string, partial: Partial<PersistedTabState>): void {
    const current = readEnvironmentTabState(environmentId) ?? DEFAULT_STATE;
    writeEnvironmentTabState(environmentId, { ...current, ...partial });
}

const VALID_TAB_NAMES = new Set<string>(["code", "conversation", "file", "environment", "logs"]);
const VALID_VIEW_MODES = new Set<string>(["details", "vscode", "changes"]);
const VALID_RIGHT_PANEL_TABS = new Set<string>(["changes", "all-files", "comments"]);
const VALID_CHANGES_SCOPES = new Set<string>(["uncommitted", "all"]);
const VALID_DRAWER_TABS = new Set<string>(["ports-services", "tasks", "terminal"]);

function isPersistedTabState(value: unknown): value is PersistedTabState {
    if (typeof value !== "object" || value === null) return false;
    const obj = value as Record<string, unknown>;

    if (!VALID_TAB_NAMES.has(obj.activeTab as string)) return false;
    if (typeof obj.selectedFilePath !== "string" && obj.selectedFilePath !== null) return false;
    if (typeof obj.isEnvironmentTabOpen !== "boolean") return false;
    if (!VALID_VIEW_MODES.has(obj.viewMode as string)) return false;

    // activeLogView is either null or a typed discriminated union
    if (obj.activeLogView !== null) {
        if (typeof obj.activeLogView !== "object") return false;
        const logView = obj.activeLogView as Record<string, unknown>;
        if (logView.type === "service") {
            if (typeof logView.serviceId !== "string") return false;
            if (typeof logView.environmentId !== "string") return false;
        } else if (logView.type === "task-run") {
            if (typeof logView.taskId !== "string") return false;
            if (typeof logView.runId !== "string") return false;
            if (typeof logView.environmentId !== "string") return false;
        } else {
            return false;
        }
    }

    // Sidebar tab fields — optional for backward compatibility with older
    // persisted data. When missing, mergeEnvironmentTabState fills defaults.
    if ("rightPanelTab" in obj && !VALID_RIGHT_PANEL_TABS.has(obj.rightPanelTab as string)) return false;
    if ("changesScope" in obj && !VALID_CHANGES_SCOPES.has(obj.changesScope as string)) return false;
    if ("drawerTab" in obj) {
        const dt = obj.drawerTab as string;
        // Accept static tabs and dynamic terminal-{id} tabs
        if (!VALID_DRAWER_TABS.has(dt) && !dt.startsWith("terminal-")) return false;
    }

    if ("selectedExecutionId" in obj && typeof obj.selectedExecutionId !== "string" && obj.selectedExecutionId !== null)
        return false;

    return true;
}

/**
 * Hook that debounces partial writes to per-environment tab state in sessionStorage.
 *
 * Returns `persist(partial)` — call it whenever a subset of tab state changes.
 * Writes are debounced (300ms) and flushed on unmount.
 *
 * Also returns `initialState` — the persisted state read synchronously on first render.
 */
export function useEnvironmentTabState(environmentId: string) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingRef = useRef<Partial<PersistedTabState> | null>(null);

    // Read initial state once (synchronous — sessionStorage is sync).
    const initialState = useRef<PersistedTabState | null>(readEnvironmentTabState(environmentId)).current;

    const flush = useCallback(() => {
        if (pendingRef.current) {
            mergeEnvironmentTabState(environmentId, pendingRef.current);
            pendingRef.current = null;
        }
    }, [environmentId]);

    const persist = useCallback(
        (partial: Partial<PersistedTabState>) => {
            pendingRef.current = { ...(pendingRef.current ?? {}), ...partial };
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                flush();
                timerRef.current = null;
            }, 300);
        },
        [flush],
    );

    // Flush pending writes on unmount so state isn't lost when navigating away.
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            flush();
        };
    }, [flush]);

    return { initialState, persist };
}
